import React, { useState } from 'react';
import {
    Card, Typography, Button, Select, Table, Tag, Modal,
    message, Row, Col, Statistic, Alert, Space, Descriptions, Grid
} from 'antd';
import {
    LockOutlined, EyeOutlined, ExclamationCircleOutlined,
    CheckCircleOutlined, DollarOutlined, FileTextOutlined
} from '@ant-design/icons';
import client from '../api/client';

const { Title, Text } = Typography;
const { confirm } = Modal;

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) => ({
    label: String(currentYear - i),
    value: currentYear - i,
}));

const fmt = (val) =>
    `₱${Number(val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const YearEndClosingPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const [year, setYear] = useState(currentYear - 1);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [closingResult, setClosingResult] = useState(null);
    const [resultVisible, setResultVisible] = useState(false);

    const loadPreview = async () => {
        setPreviewLoading(true);
        setPreview(null);
        try {
            const { data } = await client.get('/accounting/closing/preview', { params: { year } });
            setPreview(data);
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to load preview');
        } finally {
            setPreviewLoading(false);
        }
    };

    const runClosing = () => {
        if (!preview) {
            message.warning('Please preview first before running year-end closing.');
            return;
        }

        confirm({
            title: `Run Year-End Closing for FY${year}?`,
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>This will:</p>
                    <ul style={{ paddingLeft: 20 }}>
                        <li>Zero out all Revenue and Expense accounts</li>
                        <li>Transfer net {preview.net_income >= 0 ? 'income' : 'loss'} of <strong>{fmt(preview.net_income)}</strong> to Retained Earnings</li>
                        <li>Close all open accounting periods for {year}</li>
                        <li>Create a posted closing journal entry</li>
                    </ul>
                    <Alert
                        type="warning"
                        showIcon
                        message="This action cannot be easily undone. Ensure all transactions for the year are posted before proceeding."
                        style={{ marginTop: 12 }}
                    />
                </div>
            ),
            okText: `Close FY${year}`,
            okType: 'danger',
            cancelText: 'Cancel',
            width: 520,
            onOk: async () => {
                try {
                    const { data } = await client.post('/accounting/closing/close-year', { year });
                    setClosingResult(data);
                    setResultVisible(true);
                    setPreview(null);
                    message.success(`FY${year} closed successfully!`);
                } catch (err) {
                    message.error(err.response?.data?.message || 'Year-end closing failed');
                }
            },
        });
    };

    const revenueColumns = [
        { title: 'Code', dataIndex: 'code', width: 100 },
        { title: 'Account', dataIndex: 'name' },
        { title: 'Revenue', align: 'right', render: (_, r) => fmt(r.total_credit - r.total_debit) },
    ];

    const expenseColumns = [
        { title: 'Code', dataIndex: 'code', width: 100 },
        { title: 'Account', dataIndex: 'name' },
        { title: 'Expense', align: 'right', render: (_, r) => fmt(r.total_debit - r.total_credit) },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32 }}>
                <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Year-End Closing</Title>
                <Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>
                    Close a fiscal year and transfer P&L to Retained Earnings.
                </Text>
            </div>

            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 24, borderRadius: 12 }}
                message="Before running year-end closing"
                description="Ensure all invoices, purchases, and journal entries for the year are posted. The closing process will zero out Revenue and Expense accounts and post net income/loss to Retained Earnings."
            />

            <Card className="premium-card" style={{ marginBottom: 24 }}>
                <Space size="middle" wrap>
                    <div>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12, textTransform: 'uppercase' }}>Fiscal Year</Text>
                        <Select
                            value={year}
                            onChange={(v) => { setYear(v); setPreview(null); }}
                            options={yearOptions}
                            style={{ width: 160, height: 40 }}
                        />
                    </div>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={loadPreview}
                        loading={previewLoading}
                        style={{ height: 40, borderRadius: 8, marginTop: 22 }}
                    >
                        Preview Closing
                    </Button>
                    <Button
                        type="primary"
                        danger
                        icon={<LockOutlined />}
                        onClick={runClosing}
                        disabled={!preview}
                        style={{ height: 40, borderRadius: 8, marginTop: 22 }}
                    >
                        Run Year-End Closing
                    </Button>
                </Space>
            </Card>

            {preview && (
                <>
                    <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={8}>
                            <Card className="premium-card">
                                <Statistic
                                    title="Total Revenue"
                                    value={preview.total_revenue}
                                    prefix="₱"
                                    precision={2}
                                    valueStyle={{ color: '#059669' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card className="premium-card">
                                <Statistic
                                    title="Total Expenses"
                                    value={preview.total_expense}
                                    prefix="₱"
                                    precision={2}
                                    valueStyle={{ color: '#dc2626' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card className="premium-card">
                                <Statistic
                                    title="Net Income / (Loss)"
                                    value={Math.abs(preview.net_income)}
                                    prefix={preview.net_income < 0 ? '-₱' : '₱'}
                                    precision={2}
                                    valueStyle={{ color: preview.net_income >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[24, 24]}>
                        <Col xs={24} lg={12}>
                            <Card className="premium-card" title={<span style={{ fontWeight: 700 }}>Revenue Accounts</span>} bodyStyle={{ padding: 0 }}>
                                <Table
                                    dataSource={preview.revenue_items}
                                    columns={revenueColumns}
                                    rowKey="chart_of_account_id"
                                    pagination={false}
                                    size="middle"
                                    summary={() => (
                                        <Table.Summary.Row style={{ fontWeight: 700, background: '#f0fdf4' }}>
                                            <Table.Summary.Cell colSpan={2}>Total Revenue</Table.Summary.Cell>
                                            <Table.Summary.Cell align="right">{fmt(preview.total_revenue)}</Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    )}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card className="premium-card" title={<span style={{ fontWeight: 700 }}>Expense Accounts</span>} bodyStyle={{ padding: 0 }}>
                                <Table
                                    dataSource={preview.expense_items}
                                    columns={expenseColumns}
                                    rowKey="chart_of_account_id"
                                    pagination={false}
                                    size="middle"
                                    summary={() => (
                                        <Table.Summary.Row style={{ fontWeight: 700, background: '#fef2f2' }}>
                                            <Table.Summary.Cell colSpan={2}>Total Expenses</Table.Summary.Cell>
                                            <Table.Summary.Cell align="right">{fmt(preview.total_expense)}</Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    )}
                                />
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            <Modal
                open={resultVisible}
                onCancel={() => setResultVisible(false)}
                footer={<Button type="primary" onClick={() => setResultVisible(false)}>Done</Button>}
                title={<span><CheckCircleOutlined style={{ color: '#059669', marginRight: 8 }} />Year-End Closing Complete</span>}
                width={560}
            >
                {closingResult && (
                    <div>
                        <Alert
                            type="success"
                            showIcon
                            message={`FY${closingResult.year} has been successfully closed.`}
                            style={{ marginBottom: 20 }}
                        />
                        <Descriptions bordered column={1} size="middle">
                            <Descriptions.Item label="Fiscal Year">{closingResult.year}</Descriptions.Item>
                            <Descriptions.Item label="Total Revenue">
                                <Text style={{ color: '#059669', fontWeight: 600 }}>{fmt(closingResult.total_revenue)}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Total Expenses">
                                <Text style={{ color: '#dc2626', fontWeight: 600 }}>{fmt(closingResult.total_expense)}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Net Income / (Loss)">
                                <Text style={{ color: closingResult.net_income >= 0 ? '#059669' : '#dc2626', fontWeight: 700, fontSize: 16 }}>
                                    {fmt(closingResult.net_income)}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Closing Journal Entry">
                                {closingResult.closing_entry?.reference_number}
                            </Descriptions.Item>
                            <Descriptions.Item label="Periods Closed">{closingResult.periods_closed}</Descriptions.Item>
                        </Descriptions>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default YearEndClosingPage;
